import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TransformWrapper } from 'react-zoom-pan-pinch';

import {
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { fabric } from 'fabric';
import {
  studentWorkUpdateMasteryGradeAndPublish,
  useStudentTagAssignmentDetail
} from 'sdk';

import { ReactComponent as PublishIcon } from 'pages/Teachers/ClassroomInsights/assets/publish-icon.svg';
import { ReactComponent as InfoIcon } from 'pages/Teachers/ClassroomInsights/assets/tomorrow-focus-icon.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import {
  GRADES_MAPPER,
  MASTERY_LEVEL_TO_LABEL_MAPPER,
  MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT,
  STATUS_ICON_MAPPER
} from 'pages/Teachers/ClassroomInsights/constants';
import { shouldDisablePublish } from 'pages/Teachers/ClassroomInsights/utils';
import EditingCanvas from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/components/FeedbackEditingTool/components/EditingCanvas';
import {
  useEditingContext,
  useFastContext
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/utilsEditingContext';
import { UNABLE_TO_DIAGNOSE } from 'pages/Teachers/Insights/constants';
import EditorField from 'pages/Teachers/shared/EditorField';
import PreviewField from 'pages/Teachers/shared/PreviewField';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyError, notifySuccess } from 'utils/notifications';

import ControllsOverImage from 'components/ControllsOverImage';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const tooltipUseStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#333867',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Nunito'
  }
});

const StudentWorkDialog = ({
  currentStudent,
  objectiveValueType,
  status,
  onClose,
  onNext,
  onPrev,
  onActionSuccess,
  sectionId
}) => {
  const user = useContext(UserContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [overallMastery, setOverallMastery] = useState('');
  const [grade, setGrade] = useState('');
  const [explanation, setExplanation] = useState('');
  const [isEditingExplanation, setIsEditingExplanation] = useState(false);
  const [prevId, setPrevId] = useState(null);
  const [showStickers, setShowStickers] = useState(false);

  const tooltipStyles = tooltipUseStyles();
  const editingContext = useEditingContext();
  const fastContext = useFastContext();

  const recorder = { isVisible: false };
  const studentId = currentStudent?.student_tag_assignment_id;
  const currentOptions = GRADES_MAPPER[objectiveValueType] || [];
  const isNumberFieldRubric =
    objectiveValueType === 'custom' || objectiveValueType === 'number';

  const { data, loading, refetch } = useStudentTagAssignmentDetail({
    studentTagAssignmentId: studentId
  });

  const [isPublishDisabled, setIsPublishDisabled] = useState(false);

  const files = data?.student_works.map((student_work) => student_work.url);
  const totalPages = files?.length;
  const currentPageData = data?.student_works[currentPage];

  // Sync local state when new student data loads
  useEffect(() => {
    if (data) {
      setOverallMastery(data.overall_mastery || '');
      const currentStudentWork = data.student_works?.[currentPage];
      setGrade(currentStudentWork?.grade ?? '');
      setExplanation(currentStudentWork?.ai_explanation ?? '');
      setIsEditingExplanation(false);
    }
  }, [data, currentPage]);

  useEffect(() => {
    if (editingContext?.canvas && currentPageData?.url) {
      fabric.Image.fromURL(
        currentPageData.url,
        (img) => {
          const canvas = editingContext.canvas;

          const baseWidth = 380;
          const scale = baseWidth / img.width;
          const baseHeight = img.height * scale;

          canvas.setWidth(baseWidth);
          canvas.setHeight(baseHeight);
          canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
            scaleX: scale,
            scaleY: scale,
            originX: 'left',
            originY: 'top'
          });
        },
        { crossOrigin: 'anonymous' }
      );
    }
  }, [currentPageData?.url, editingContext?.canvas]);

  if (studentId !== prevId) {
    setCurrentPage(0);
    setPrevId(studentId);
  }

  const handleMasteryChange = (e) => setOverallMastery(e.target.value);
  const handleGradeChange = (e) => setGrade(e.target.value);
  const handleExplanationChange = ({ newExplanation }) => {
    const normalizedExplanation = newExplanation.replace(/\\n/g, '\n');
    setExplanation(normalizedExplanation);

    if (!isEditingExplanation) {
      setIsEditingExplanation(true);
    }
  };

  const handlePublishing = async () => {
    setIsPublishDisabled(true);

    const payload = {
      student_tag_assignment_id: studentId,
      mastery_level: overallMastery,
      grade: grade,
      ai_explanation: explanation,
      should_publish: !disablePublish
    };

    const response = await studentWorkUpdateMasteryGradeAndPublish({
      studentWorkId: data?.student_works[currentPage]?.id,
      data: payload
    });

    if (response.success) {
      notifySuccess(
        !disablePublish
          ? 'Student work updated and published successfully!'
          : 'Student work updated successfully!'
      );

      onActionSuccess();
      refetch();
      setIsEditingExplanation(false);

      trackMixpanelEvent(user.id, 'Student work published', {
        feature: 'live classroom insights',
        view: 'student work dialog',
        action: 'Inline Edit and Publish',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      });
    } else {
      notifyError('Failed to save changes. Please try again.');
    }

    setIsPublishDisabled(false);
  };

  const currentAiExplanation =
    data?.student_works?.[currentPage]?.ai_explanation || '';
  const hasValidAiFeedback =
    currentAiExplanation !== '' &&
    currentAiExplanation !==
      'There was an issue processing this upload. Please try again.';

  const disablePublish = useMemo(
    () =>
      shouldDisablePublish({
        status: currentStudent?.tracker_score_student_status,
        overallResponseStatus: currentStudent?.tag_description
      }),
    [currentStudent]
  );

  const handleOnClose = () => {
    setIsEditingExplanation(false);
    onClose();
  };

  return (
    <Dialog
      open={!!studentId}
      onClose={() => {
        handleOnClose();
      }}
      fullWidth
      PaperProps={{ style: { overflow: 'visible', borderRadius: '12px' } }}
    >
      {!loading && (
        <>
          <Tooltip
            title="Previous Student"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton
              className={styles.globalPrev}
              onClick={() => {
                onPrev();
                setIsPublishDisabled(false);
              }}
            >
              <ChevronLeftIcon style={{ fontSize: 40, color: 'white' }} />
            </IconButton>
          </Tooltip>

          <Tooltip
            title="Next Student"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton
              className={styles.globalNext}
              onClick={() => {
                onNext();
                setIsPublishDisabled(false);
              }}
            >
              <ChevronRightIcon style={{ fontSize: 40, color: 'white' }} />
            </IconButton>
          </Tooltip>
        </>
      )}

      <div className={styles.dialogContainer}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.studentTitle}>
            {data?.student_name || 'Loading...'}
            {STATUS_ICON_MAPPER[status]}
          </span>
          <IconButton
            onClick={() => {
              handleOnClose();
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {loading ? (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={styles.imageSection}>
              {files?.length >= 1 && (
                <div className={styles.feedbackImage} id="image-container">
                  <TransformWrapper
                    initialScale={1}
                    centerOnInit={true}
                    limitToBound={true}
                    options={{
                      maxScale: 11,
                      disabled: false
                    }}
                    zoomIn={{ step: 15 }}
                    zoomOut={{ step: 15 }}
                    wheel={{ disabled: true }}
                    pan={{
                      paddingSize: 0,
                      disabled:
                        editingContext?.objectDeletion?.hasSelectedObject ||
                        editingContext?.draw?.isActive ||
                        editingContext?.cropping?.active
                    }}
                    doubleClick={{ disabled: true }}
                  >
                    {({ scale, zoomOut, zoomIn, resetTransform }) => {
                      return (
                        <>
                          <EditingCanvas
                            editingContext={editingContext}
                            resetTransform={resetTransform}
                            fastContext={fastContext}
                          />
                          <ControllsOverImage
                            recorder={recorder}
                            showStickers={showStickers}
                            closeStickers={() => setShowStickers(false)}
                            editingContext={editingContext}
                            fastContext={fastContext}
                            zoom={{ zoomIn, zoomOut, scale }}
                            positionRight={true}
                          />
                        </>
                      );
                    }}
                  </TransformWrapper>
                </div>
              )}

              {totalPages > 1 && (
                <div className={styles.paginationControls}>
                  <IconButton
                    className={styles.pageArrow}
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <span>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <IconButton
                    className={styles.pageArrow}
                    disabled={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </div>
              )}
            </div>

            <div className={styles.insightsSection}>
              <div className={styles.insightHeader}>
                <div>
                  <InfoIcon />
                  <span className={styles.insightsTitle}>AI Insights</span>
                </div>
              </div>

              <Divider />

              <div className={styles.dropdownRow}>
                <FormControl
                  variant="outlined"
                  size="small"
                  className={styles.dropdownControl}
                >
                  <Select
                    displayEmpty
                    value={overallMastery}
                    onChange={handleMasteryChange}
                    IconComponent={ExpandMoreIcon}
                    className={styles.dropdownSelect}
                    renderValue={(selected) =>
                      selected === '' || selected === UNABLE_TO_DIAGNOSE ? (
                        <span className={styles.placeholderText}>
                          Select Support
                        </span>
                      ) : (
                        MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT[selected] ||
                        MASTERY_LEVEL_TO_LABEL_MAPPER[selected]
                      )
                    }
                  >
                    {Object.entries(MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT).map(
                      ([value, label]) => {
                        if (value === UNABLE_TO_DIAGNOSE) return null;
                        return (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  className={styles.dropdownControl}
                >
                  {isNumberFieldRubric ? (
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder={
                        objectiveValueType === 'number'
                          ? '0-100'
                          : 'Enter value'
                      }
                      value={grade}
                      type="number"
                      onChange={handleGradeChange}
                    />
                  ) : (
                    <Select
                      displayEmpty
                      value={grade}
                      onChange={handleGradeChange}
                      IconComponent={ExpandMoreIcon}
                      className={styles.dropdownSelect}
                      renderValue={(selected) => {
                        if (
                          selected === '' ||
                          selected === null ||
                          selected === undefined
                        )
                          return (
                            <span className={styles.placeholderText}>
                              Select Grade
                            </span>
                          );

                        const match = currentOptions.find(
                          (opt) => String(opt.value) === String(selected)
                        );
                        return (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <CheckGradeIcon
                              grade={selected}
                              objectiveValueType={objectiveValueType}
                            />
                            {match?.label || selected}
                          </div>
                        );
                      }}
                    >
                      {currentOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <CheckGradeIcon
                            grade={opt.value}
                            objectiveValueType={objectiveValueType}
                          />
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </div>

              {hasValidAiFeedback ? (
                !isEditingExplanation && explanation ? (
                  <div
                    className={styles.explanationText}
                    onClick={() => setIsEditingExplanation(true)}
                  >
                    <PreviewField initialValue={explanation} />
                  </div>
                ) : (
                  <div className={styles.editExplanationBox}>
                    <div className={styles.editExplanationHeader}>
                      Provide your explanation below to help me get it right
                      next time!
                    </div>
                    <EditorField
                      initialValue={explanation}
                      onChange={(newExplanation) => {
                        handleExplanationChange({
                          newExplanation: newExplanation
                        });
                      }}
                      hideMathKeyboard={true}
                      isExplanationField={true}
                    />
                  </div>
                )
              ) : (
                <div
                  className={styles.explanationText}
                  style={{ cursor: 'default' }}
                >
                  {currentAiExplanation || (
                    <span className={styles.noExplanation}>
                      No AI Insights found
                    </span>
                  )}
                </div>
              )}

              <button
                className={styles.publishBtn}
                onClick={handlePublishing}
                disabled={
                  isPublishDisabled || grade === '' || overallMastery === ''
                }
              >
                <PublishIcon />
                <span className={styles.publishBtnText}>PUBLISH</span>
              </button>

              <span className={styles.footerText}>
                Update assignment status in the student app based on the student
                mastery.
              </span>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
export default StudentWorkDialog;
